import { api } from '@guild/core'

/**
 * The API, typed from the contracts rather than from hope.
 *
 * Every response is parsed with the same Zod schema the server validated it
 * against (`packages/core/src/api/`), so a contract that changes is a compile
 * error here and a loud refusal at runtime, not a screen quietly rendering
 * `undefined`. Requests carry the session cookie and nothing else.
 *
 * Failures are **codes**, never sentences: the server sends `{ error }` and the
 * screens look the code up in `guild-voice.json` in the reader's locale, which
 * is the same route every other string takes (Q12.2). A refusal that carries a
 * log event carries it through unchanged, so the island screen can say why in
 * the guild's voice instead of showing a box.
 */

/** Enough of a Zod schema to parse a response, without depending on Zod here. */
type Parser<T> = {
  safeParse(value: unknown): { success: true; data: T } | { success: false }
}

/** The value a schema parses to, so the client's answers are named by the contract. */
type Parsed<S> = S extends Parser<infer T> ? T : never

export type ApiFailure = {
  readonly ok: false
  /** 0 when the request never reached the server. */
  readonly status: number
  readonly error: string
  readonly log?: api.ApiLogLine
}

export type ApiResult<T> = { readonly ok: true; readonly value: T } | ApiFailure

/** The code a request that never arrived reports. Rendered as `errorOffline`. */
export const OFFLINE = 'offline'
/** The code a response the contract does not recognise reports. */
export const BAD_SHAPE = 'response-shape'

async function request<T>(
  path: string,
  init: RequestInit | undefined,
  parser: Parser<T>,
): Promise<ApiResult<T>> {
  let response: Response
  try {
    response = await fetch(path, {
      credentials: 'same-origin',
      headers: init?.body === undefined ? {} : { 'content-type': 'application/json' },
      ...init,
    })
  } catch {
    return { ok: false, status: 0, error: OFFLINE }
  }

  const payload: unknown = await response.json().catch(() => null)
  if (!response.ok) {
    const failure = api.apiErrorSchema.safeParse(payload)
    if (!failure.success) return { ok: false, status: response.status, error: BAD_SHAPE }
    const { error, log } = failure.data
    return log === undefined
      ? { ok: false, status: response.status, error }
      : { ok: false, status: response.status, error, log }
  }

  const parsed = parser.safeParse(payload)
  if (!parsed.success) return { ok: false, status: response.status, error: BAD_SHAPE }
  return { ok: true, value: parsed.data }
}

const post = <T>(path: string, body: unknown, parser: Parser<T>) =>
  request(path, { method: 'POST', body: JSON.stringify(body) }, parser)

/** What a draft is saved from: the body, the enclosures, and the island it is about (Q17.1). */
export type DraftToSave = {
  readonly body: unknown
  readonly attachments: api.ApiAttachments
  readonly dayAbout: string
}

/**
 * What a client answers, named so a second one can be held to it.
 *
 * The network client below is one implementation; the practice shore's local
 * client (`practice.ts`, #134) is another, backed by memory and the rules in
 * `@guild/core`. The screens are written against this and never ask which one
 * they hold -- so anything a screen needs is added here, for both, rather than
 * branched on in the screen.
 */
export type ApiClient = {
  me(): Promise<ApiResult<Parsed<typeof api.meResponseSchema>>>
  register(
    username: string,
    password: string,
  ): Promise<ApiResult<Parsed<typeof api.sessionResponseSchema>>>
  login(
    username: string,
    password: string,
  ): Promise<ApiResult<Parsed<typeof api.sessionResponseSchema>>>
  logout(): Promise<ApiResult<Parsed<typeof api.okSchema>>>

  island(day?: string): Promise<ApiResult<api.ApiIslandState>>
  move(day: string, direction: string): Promise<ApiResult<api.ApiMoveResponse>>
  /** The sinking day's landing: which shore the boat puts in at (Q21.1). */
  returnLanding(day: string, shoreIndex: number): Promise<ApiResult<api.ApiIslandState>>

  draft(): Promise<ApiResult<Parsed<typeof api.draftResponseSchema>>>
  saveDraft(draft: DraftToSave): Promise<ApiResult<Parsed<typeof api.draftResponseSchema>>>
  send(dayAbout: string): Promise<ApiResult<Parsed<typeof api.sendResponseSchema>>>
  reroll(): Promise<ApiResult<Parsed<typeof api.rerollResponseSchema>>>

  post(): Promise<ApiResult<Parsed<typeof api.postResponseSchema>>>
  letter(publicId: string): Promise<ApiResult<api.ApiInboxLetter>>
  chart(publicId: string): Promise<ApiResult<Parsed<typeof api.chartResponseSchema>>>
  mark(
    publicId: string,
    mark: 'trustworthy' | 'false' | null,
  ): Promise<ApiResult<Parsed<typeof api.markResponseSchema>>>

  atlas(): Promise<ApiResult<api.ApiAtlas>>
  shareCard(day: string): Promise<ApiResult<Parsed<typeof api.shareCardResponseSchema>>>
  /** The card as text. The one endpoint that answers with words, not ids. */
  shareText(day: string): Promise<ApiResult<string>>
}

export function createApi(): ApiClient {
  return {
    me: () => request('/api/auth/me', undefined, api.meResponseSchema),
    register: (username, password) =>
      post('/api/auth/register', { username, password }, api.sessionResponseSchema),
    login: (username, password) =>
      post('/api/auth/login', { username, password }, api.sessionResponseSchema),
    logout: () => post('/api/auth/logout', {}, api.okSchema),

    island: (day) =>
      request(
        day === undefined ? '/api/island' : `/api/island/${day}`,
        undefined,
        api.islandStateResponseSchema,
      ),
    move: (day, direction) =>
      post(`/api/island/${day}/move`, { direction }, api.moveResponseSchema),
    returnLanding: (day, shoreIndex) =>
      post(`/api/island/${day}/return`, { shoreIndex }, api.returnLandingResponseSchema),

    draft: () => request('/api/letter/draft', undefined, api.draftResponseSchema),
    saveDraft: (draft) =>
      request(
        '/api/letter/draft',
        { method: 'PUT', body: JSON.stringify(draft) },
        api.draftResponseSchema,
      ),
    send: (dayAbout) => post('/api/letter/send', { dayAbout }, api.sendResponseSchema),
    reroll: () => post('/api/letter/reroll', {}, api.rerollResponseSchema),

    post: () => request('/api/post', undefined, api.postResponseSchema),
    letter: (publicId) => request(`/api/post/${publicId}`, undefined, api.inboxLetterSchema),
    chart: (publicId) => post(`/api/post/${publicId}/chart`, {}, api.chartResponseSchema),
    mark: (publicId, mark) => post(`/api/post/${publicId}/mark`, { mark }, api.markResponseSchema),

    atlas: () => request('/api/atlas', undefined, api.atlasResponseSchema),
    shareCard: (day) =>
      request(`/api/day/${day}/share.json`, undefined, api.shareCardResponseSchema),

    shareText: async (day) => {
      let response: Response
      try {
        response = await fetch(`/api/day/${day}/share.txt`, { credentials: 'same-origin' })
      } catch {
        return { ok: false, status: 0, error: OFFLINE }
      }
      if (!response.ok) {
        const payload: unknown = await response.json().catch(() => null)
        const failure = api.apiErrorSchema.safeParse(payload)
        return {
          ok: false,
          status: response.status,
          error: failure.success ? failure.data.error : BAD_SHAPE,
        }
      }
      return { ok: true, value: await response.text() }
    },
  }
}
